import { KpiData } from '@/app/features/hse/pages/reportes/pages/reportes-estadisticas/model/kpi-data';

import { KPI } from '@/app/models/interface/kpi.model';
import { environment } from '@/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map } from 'rxjs';
import { handleError } from '../tools/handle-error.tools';
import {
  PaginatorModel,
  HasPagination,
} from '@/app/models/interface/paginator.model';
import { PlanillaEstadistica } from '@/app/models/interface/planilla-estadistica.model';
import { CustomResponse } from '@/app/models/interface/response.model';
import { Usuario } from '@/app/models/interface/usuario.model';

export interface Data {
  kpisData: KPIStat[];
  horasKm: HsKmHsCap;
  porProyecto: PorProyecto[];
  proyectos: ProyectoData[];
  estadisticas: KpiData[];
  kpisDataV2: KpiDataV2[];
}

interface EmpresaData {
  id: number;
  nombre: string;
  value: number;
}

export interface Data {
  mes: number;
  value: number;
  empresas: EmpresaData[];
  promedioEmpresas: number;
}

export interface ProyectoData {
  id: number;
  nombre: string;
}

interface Data2 {
  proyecto: ProyectoData;
  data: Data[];
}

export interface KpiDataV2 {
  kpi: KPI;
  data: Data2[];
}

export interface KPIStat {
  kpi: KPI;
  data: {
    mes: number;
    value: number;
    proyectos: { id: number; nombre: string; value: number }[];
  }[];
}

export interface PorProyecto {
  hs: number;
  km: number;
  proyectos: {
    id: number;
    nombre: string;
    hs: number;
    km: number;
  }[];
}

export interface ProyectoData {
  id: number;
  nombre: string;
  valoresHs: number[];
  valoresKm: number[];
  totalHs: number;
  totalKm: number;
}

export interface Cambios {
  comentario: string;
  usuarioCarga: Usuario;
  fecha_carga: Date | string;
}

export interface HsKmHsCap {
  horas: {
    value: number;
  }[];
  km: {
    value: number;
  }[];
  hsCap: HsCap[];
}

export interface HsCap {
  value: number;
  items: any[];
  areas: { nombre: string; value: number }[];
}

@Injectable({ providedIn: 'root' })
export class PlanillaEstadisticaService {
  private readonly http = inject(HttpClient);
  private readonly url = environment.api + 'planilla-estadistica/';

  create(data: Partial<PlanillaEstadistica>) {
    return this.http
      .post<CustomResponse<PlanillaEstadistica>>(this.url, data)
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          const d = data.result;
          return {
            ...d,
          };
        }),
      );
  }

  findAll(params?: HttpParams) {
    return this.http
      .get<
        CustomResponse<PaginatorModel<PlanillaEstadistica[], HasPagination>>
      >(this.url, {
        params,
      })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          const d = data.result;
          if (d && Array.isArray(d.data)) {
            return {
              ...d,
              data: d.data.map((d) => ({
                ...d,
              })),
            };
          }
          return d;
        }),
      );
  }

  findOne(id: number) {
    const url = this.url + id;
    return this.http.get<CustomResponse<PlanillaEstadistica>>(url).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const d = data.result;
        return {
          ...d,
        };
      }),
    );
  }

  update(tipo: Partial<PlanillaEstadistica>) {
    const url = this.url + tipo.id;
    return this.http.put<CustomResponse<Cambios[]>>(url, tipo).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  changeState(state: boolean, id: number) {
    const url = this.url + id;
    return this.http
      .patch<CustomResponse<PlanillaEstadistica>>(url, { state })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          const d = data.result;
          return {
            ...d,
          };
        }),
      );
  }

  getEstadisticasKPIs(params: HttpParams) {
    const url = this.url + 'stats';
    return this.http.get<CustomResponse<Data>>(url, { params }).pipe(
      catchError((err) => handleError(err)),
      map((data) => data.result),
    );
  }
}
